import React from 'react';
import { RefreshCw } from 'lucide-react';

const OrderEntry = ({
  selectedCoin,
  livePrice,
  portfolio,
  side,
  setSide,
  orderType,
  setOrderType,
  limitPrice,
  setLimitPrice,
  quantity,
  setQuantity,
  useSLTP,
  setUseSLTP,
  stopLoss,
  setStopLoss,
  takeProfit,
  setTakeProfit,
  handlePercentageSelect,
  handlePlaceOrder,
  tradeLoading,
  formError,
  setFormError,
  formSuccess,
  setFormSuccess,
  currentHoldingQty,
}) => {
  return (
    <aside className="trade-panel">
      <div className="trade-side-selector">
        <button 
          className={`side-btn buy-side ${side === 'BUY' ? 'active-buy' : ''}`}
          onClick={() => {
            setSide('BUY');
            setFormError('');
            setFormSuccess('');
          }}
        >
          Buy
        </button>
        <button 
          className={`side-btn sell-side ${side === 'SELL' ? 'active-sell' : ''}`}
          onClick={() => {
            setSide('SELL');
            setFormError('');
            setFormSuccess('');
          }}
        >
          Sell
        </button>
      </div>

      <form onSubmit={handlePlaceOrder} className="trade-form">
        {/* Order Type Selector */}
        <div className="form-row">
          <label>Order Type</label>
          <div className="order-type-selector">
            <button 
              type="button" 
              className={`type-btn ${orderType === 'MARKET' ? 'active-type' : ''}`}
              onClick={() => setOrderType('MARKET')}
            >
              Market
            </button>
            <button 
              type="button" 
              className={`type-btn ${orderType === 'LIMIT' ? 'active-type' : ''}`}
              onClick={() => {
                setOrderType('LIMIT');
                setLimitPrice(livePrice ? livePrice.toString() : '');
              }}
            >
              Limit
            </button>
          </div>
        </div>

        {/* Price Input */}
        <div className="form-row">
          <label>Price (USD)</label>
          <div className="input-with-label">
            <input 
              type="number"
              step="any"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              disabled={orderType === 'MARKET'}
              className="trade-input-field"
              placeholder="Price"
            />
            {orderType === 'MARKET' && <span className="input-badge">MARKET</span>}
          </div>
        </div>

        {/* Quantity Input */}
        <div className="form-row">
          <label>Amount ({selectedCoin?.symbol.toUpperCase()})</label>
          <input 
            type="number"
            step="any"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="trade-input-field"
            placeholder="0.00"
            required
          />
        </div>

        {/* Quick Size selection percentages */}
        <div className="percent-row">
          {[0.25, 0.50, 0.75, 1.0].map((pct) => (
            <button 
              key={pct} 
              type="button" 
              className="pct-btn"
              onClick={() => handlePercentageSelect(pct)}
            >
              {pct * 100}%
            </button>
          ))}
        </div>

        {/* Estimated Order Value */}
        <div className="estimation-row">
          <span>Order Value:</span>
          <strong>
            ${((parseFloat(quantity) || 0) * (orderType === 'LIMIT' ? parseFloat(limitPrice) || 0 : livePrice || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
          </strong>
        </div>

        {/* SL/TP Checkbox and Inputs */}
        {side === 'BUY' && (
          <div className="sltp-section">
            <label className="checkbox-container">
              <input 
                type="checkbox" 
                checked={useSLTP} 
                onChange={(e) => setUseSLTP(e.target.checked)}
              />
              <span>Stop Loss / Take Profit</span>
            </label>
            
            {useSLTP && (
              <div className="sltp-inputs animated-sltp">
                <div className="sltp-input-row">
                  <label>Take Profit (TP)</label>
                  <input 
                    type="number" 
                    step="any"
                    value={takeProfit} 
                    onChange={(e) => setTakeProfit(e.target.value)}
                    placeholder="Profit trigger price"
                    className="trade-input-field sm-input"
                  />
                </div>
                <div className="sltp-input-row" style={{ marginTop: '8px' }}>
                  <label>Stop Loss (SL)</label>
                  <input 
                    type="number" 
                    step="any"
                    value={stopLoss} 
                    onChange={(e) => setStopLoss(e.target.value)}
                    placeholder="Loss trigger price"
                    className="trade-input-field sm-input"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Balance and Asset held info */}
        <div className="balance-info-block">
          <div className="info-line">
            <span>Available USD:</span>
            <span>${portfolio.balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="info-line">
            <span>Holdings ({selectedCoin?.symbol.toUpperCase()}):</span>
            <span>{currentHoldingQty?.toFixed(6)}</span>
          </div>
        </div>

        {/* Status alerts */}
        {formError && <div className="trade-alert error-alert">{formError}</div>}
        {formSuccess && <div className="trade-alert success-alert">{formSuccess}</div>}

        {/* CTA Buy/Sell Button */}
        <button 
          type="submit" 
          className={`submit-trade-btn ${side === 'BUY' ? 'buy-cta' : 'sell-cta'}`}
          disabled={tradeLoading}
        >
          {tradeLoading ? (
            <RefreshCw className="spinner" size={16} />
          ) : (
            `${side === 'BUY' ? 'Buy' : 'Sell'} ${selectedCoin?.symbol?.toUpperCase() || ''}`
          )}
        </button>
      </form>
    </aside>
  );
};

export default OrderEntry;
